import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { SatelliteMachineViewStore } from "../../store/SatelliteMachineViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { ShowDateTime } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";

export type SatelliteMachineViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  machineCodeValue?: { displayId: string; id: string } | null;
  machineCode: string;
  satelliteTypeValue?: { displayId: string; id: string } | null;
  satelliteType: string;
  regionValue?: { displayId: string; id: string } | null;
  region: string;
  certificateExpiresAtValue?: { displayId: string; id: string } | null;
  certificateExpiresAt: Date | null;
  authTokenLastFourValue?: { displayId: string; id: string } | null;
  authTokenLastFour: string;
  authTokenCreatedAtValue?: { displayId: string; id: string } | null;
  authTokenCreatedAt: Date | null;
  authTokenRotatedAtValue?: { displayId: string; id: string } | null;
  authTokenRotatedAt: Date | null;
  statusValue?: { displayId: string; id: string } | null;
  status: string;
  authorizedAtValue?: { displayId: string; id: string } | null;
  authorizedAt: Date | null;
  authorizedByValue: { displayId: string; id: string } | null;
  suspendedAtValue?: { displayId: string; id: string } | null;
  suspendedAt: Date | null;
  suspendedByValue: { displayId: string; id: string } | null;
  suspensionReasonValue?: { displayId: string; id: string } | null;
  suspensionReason: string;
  enabledValue?: { displayId: string; id: string } | null;
  enabled: boolean;
  allowedTenantIdsValue?: { displayId: string; id: string } | null;
  allowedTenantIds: string;
  proxyEnabledValue?: { displayId: string; id: string } | null;
  proxyEnabled: boolean;
  proxyPortValue?: { displayId: string; id: string } | null;
  proxyPort: number | undefined;
  proxyProtocolsValue?: { displayId: string; id: string } | null;
  proxyProtocols: string;
  botEnabledValue?: { displayId: string; id: string } | null;
  botEnabled: boolean;
  botMaxConcurrentValue?: { displayId: string; id: string } | null;
  botMaxConcurrent: number | undefined;
  workerEnabledValue?: { displayId: string; id: string } | null;
  workerEnabled: boolean;
  publicIpValue?: { displayId: string; id: string } | null;
  publicIp: string;
  locationValue?: { displayId: string; id: string } | null;
  location: string;
  countryCodeValue?: { displayId: string; id: string } | null;
  countryCode: string;
  lastHeartbeatAtValue?: { displayId: string; id: string } | null;
  lastHeartbeatAt: Date | null;
  lastHeartbeatMetricsValue?: { displayId: string; id: string } | null;
  lastHeartbeatMetrics: string;
  connectedAtValue?: { displayId: string; id: string } | null;
  connectedAt: Date | null;
  disconnectedAtValue?: { displayId: string; id: string } | null;
  disconnectedAt: Date | null;
  tunnelConnectedAtValue?: { displayId: string; id: string } | null;
  tunnelConnectedAt: Date | null;
  tasksRows: CrudRows;
  tasksVisibleColumns: GridColumn[];
  tasksSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  satelliteMachineViewStore: SatelliteMachineViewStore;
};

export const SatelliteMachineViewForm: React.FC<SatelliteMachineViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      name = "",
      machineCode = "",
      satelliteType = "",
      region = "",
      certificateExpiresAt = null,
      authTokenLastFour = "",
      authTokenCreatedAt = null,
      authTokenRotatedAt = null,
      status = "",
      authorizedAt = null,
      authorizedByValue,
      suspendedAt = null,
      suspendedByValue,
      suspensionReason = "",
      enabled = false,
      allowedTenantIds = "",
      proxyEnabled = false,
      proxyPort = 0,
      proxyProtocols = "",
      botEnabled = false,
      botMaxConcurrent = 0,
      workerEnabled = false,
      publicIp = "",
      location = "",
      countryCode = "",
      lastHeartbeatAt = null,
      lastHeartbeatMetrics = "{}",
      connectedAt = null,
      disconnectedAt = null,
      tunnelConnectedAt = null,
      tasksRows,
      tasksVisibleColumns,
      tasksSetColumnWiths,
      satelliteMachineViewStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="identity"
              title={langStore.groupSatelliteMachineIdentityLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("identity")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("identity")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={name}
                  label={langStore.SatelliteMachineNameLabel}
                />
                <ShowMultiLine
                  value={machineCode}
                  label={langStore.SatelliteMachineMachineCodeLabel}
                />
                <ShowMultiLine
                  value={satelliteType}
                  label={langStore.SatelliteMachineSatelliteTypeLabel}
                />
                <ShowMultiLine
                  value={region}
                  label={langStore.SatelliteMachineRegionLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="certificate"
              title={langStore.groupSatelliteMachineCertificateLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("certificate")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("certificate")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  value={certificateExpiresAt}
                  label={langStore.SatelliteMachineCertificateExpiresAtLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="authentication"
              title={langStore.groupSatelliteMachineAuthenticationLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState(
                "authentication"
              )}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("authentication")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={authTokenLastFour}
                  label={langStore.SatelliteMachineAuthTokenLastFourLabel}
                />
                <ShowDateTime
                  value={authTokenCreatedAt}
                  label={langStore.SatelliteMachineAuthTokenCreatedAtLabel}
                />
                <ShowDateTime
                  value={authTokenRotatedAt}
                  label={langStore.SatelliteMachineAuthTokenRotatedAtLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="authorization"
              title={langStore.groupSatelliteMachineAuthorizationLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("authorization")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("authorization")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={status}
                  label={langStore.SatelliteMachineStatusLabel}
                />
                <ShowDateTime
                  value={authorizedAt}
                  label={langStore.SatelliteMachineAuthorizedAtLabel}
                />
                <ShowReference
                  label={langStore.SatelliteMachineAuthorizedByLabel}
                  selectedValue={langStore.displayIdByLang(
                    authorizedByValue?.displayId
                  )}
                />
                <ShowDateTime
                  value={suspendedAt}
                  label={langStore.SatelliteMachineSuspendedAtLabel}
                />
                <ShowReference
                  label={langStore.SatelliteMachineSuspendedByLabel}
                  selectedValue={langStore.displayIdByLang(
                    suspendedByValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={suspensionReason}
                  label={langStore.SatelliteMachineSuspensionReasonLabel}
                />
                <ShowBoolean
                  value={enabled}
                  label={langStore.SatelliteMachineEnabledLabel}
                />
                <ShowMultiLine
                  value={allowedTenantIds}
                  label={langStore.SatelliteMachineAllowedTenantIdsLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="capabilities"
              title={langStore.groupSatelliteMachineCapabilitiesLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("capabilities")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("capabilities")
              }
            >
              <FormControl fullWidth>
                <ShowBoolean
                  value={proxyEnabled}
                  label={langStore.SatelliteMachineProxyEnabledLabel}
                />
                <ShowNumber
                  value={proxyPort}
                  label={langStore.SatelliteMachineProxyPortLabel}
                />
                <ShowMultiLine
                  value={proxyProtocols}
                  label={langStore.SatelliteMachineProxyProtocolsLabel}
                />
                <ShowBoolean
                  value={botEnabled}
                  label={langStore.SatelliteMachineBotEnabledLabel}
                />
                <ShowNumber
                  value={botMaxConcurrent}
                  label={langStore.SatelliteMachineBotMaxConcurrentLabel}
                />
                <ShowBoolean
                  value={workerEnabled}
                  label={langStore.SatelliteMachineWorkerEnabledLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="network"
              title={langStore.groupSatelliteMachineNetworkLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("network")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("network")
              }
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={publicIp}
                  label={langStore.SatelliteMachinePublicIpLabel}
                />
                <ShowMultiLine
                  value={location}
                  label={langStore.SatelliteMachineLocationLabel}
                />
                <ShowMultiLine
                  value={countryCode}
                  label={langStore.SatelliteMachineCountryCodeLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="monitoring"
              title={langStore.groupSatelliteMachineMonitoringLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("monitoring")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("monitoring")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  value={lastHeartbeatAt}
                  label={langStore.SatelliteMachineLastHeartbeatAtLabel}
                />
                <InputCodeEditor
                  value={lastHeartbeatMetrics}
                  onChange={() => {}}
                  label={langStore.SatelliteMachineLastHeartbeatMetricsLabel}
                  language="json"
                  readOnly={true}
                />
                <ShowDateTime
                  value={connectedAt}
                  label={langStore.SatelliteMachineConnectedAtLabel}
                />
                <ShowDateTime
                  value={disconnectedAt}
                  label={langStore.SatelliteMachineDisconnectedAtLabel}
                />
                <ShowDateTime
                  value={tunnelConnectedAt}
                  label={langStore.SatelliteMachineTunnelConnectedAtLabel}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="relations"
              title={langStore.groupSatelliteMachineRelationsLabel}
              collapsible={true}
              expanded={satelliteMachineViewStore.getCardState("relations")}
              toggleExpand={() =>
                satelliteMachineViewStore.toggleCardState("relations")
              }
            >
              <FormControl fullWidth>
                <ShowItemsCompact
                  rows={tasksRows}
                  showLabel={false}
                  label={langStore.SatelliteMachineTasksLabel}
                  columns={tasksVisibleColumns}
                  setColumnWidths={tasksSetColumnWiths}
                  entityName="SatelliteTask"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("satelliteTask", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
